import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useMenu } from '../../context/MenuContext';
import { useTheme } from '../../context/ThemeContext';
import {
    Home,
    LayoutDashboard,
    Package,
    ClipboardList,
    Users,
    Settings,
    BarChart3,
    FileText,
    User,
    LogOut,
    Menu,
    X,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    Shield,
    Tag,
    MapPin,
    Zap,
    ClipboardCheck,
    ShieldCheck,
    Boxes,
    DollarSign,
    FolderOpen,
    Truck,
    Leaf,
    Camera,
    Wrench,
    Monitor,
    FileSpreadsheet,
    Trash2,
    CalendarClock,
    Shirt,
    ArrowLeftRight,
    Inbox,
    Sun,
    Moon,
} from 'lucide-react';
import Button from './Button';
import NotificationBell from './NotificationBell';

// Mapa icono-string -> componente lucide
const ICON_MAP = {
    LayoutDashboard,
    Package,
    ClipboardList,
    Users,
    BarChart3,
    FileText,
    User,
    Tag,
    MapPin,
    Zap,
    ClipboardCheck,
    ShieldCheck,
    Boxes,
    DollarSign,
    FolderOpen,
    Truck,
    Leaf,
    Home,
    Settings,
    Camera,
    Wrench,
    Monitor,
    FileSpreadsheet,
    Trash2,
    CalendarClock,
    Shirt,
    ArrowLeftRight,
    Inbox,
};

// Agrupa la lista plana de menús (con parent_key) en un árbol de cualquier profundidad.
const buildMenuTree = (items) => {
    const nodes = new Map(items.map((it) => [it.menu_key, { ...it, children: [] }]));
    const roots = [];
    for (const node of nodes.values()) {
        if (node.parent_key && nodes.has(node.parent_key)) {
            nodes.get(node.parent_key).children.push(node);
        } else {
            roots.push(node);
        }
    }
    return roots;
};

// Render recursivo de los nodos del menú (grupos expandibles + hojas con Link).
const NavTree = ({ nodes, depth = 0, isActive, isCollapsed, expanded, onToggle, onNavigate }) => {
    if (isCollapsed) {
        // En modo colapsado solo tiene sentido mostrar las hojas (sin texto/anidado).
        const hojas = [];
        const recolectar = (list) => list.forEach((n) => (n.children.length ? recolectar(n.children) : hojas.push(n)));
        recolectar(nodes);
        return hojas.map((item) => {
            const Icon = ICON_MAP[item.icono] || FileText;
            return (
                <Link
                    key={item.menu_key}
                    to={item.path}
                    onClick={onNavigate}
                    className={`group flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                        isActive(item.path) ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    title={item.nombre}
                >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                </Link>
            );
        });
    }

    return nodes.map((item) => {
        const Icon = ICON_MAP[item.icono] || FileText;
        const tieneHijos = item.children.length > 0;
        const padding = 12 + depth * 16;

        if (tieneHijos) {
            const abierto = expanded.has(item.menu_key);
            return (
                <div key={item.menu_key}>
                    <button
                        type="button"
                        onClick={() => onToggle(item.menu_key)}
                        style={{ paddingLeft: padding }}
                        className="w-full flex items-center justify-between pr-3 py-2 text-sm font-medium rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
                    >
                        <span className="flex items-center">
                            <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                            <span className="truncate">{item.nombre}</span>
                        </span>
                        <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${abierto ? 'rotate-180' : ''}`} />
                    </button>
                    {abierto && (
                        <NavTree
                            nodes={item.children}
                            depth={depth + 1}
                            isActive={isActive}
                            isCollapsed={isCollapsed}
                            expanded={expanded}
                            onToggle={onToggle}
                            onNavigate={onNavigate}
                        />
                    )}
                </div>
            );
        }

        return (
            <Link
                key={item.menu_key}
                to={item.path}
                onClick={onNavigate}
                style={{ paddingLeft: padding }}
                className={`group flex items-center pr-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    isActive(item.path)
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
            >
                <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                <span className="truncate">{item.nombre}</span>
            </Link>
        );
    });
};

// Botón para alternar modo claro/oscuro — se usa tanto en el header mobile
// como en el desktop, por eso está aparte.
const ThemeToggleButton = ({ className = '' }) => {
    const { isDark, toggleTheme } = useTheme();
    return (
        <button
            type="button"
            onClick={toggleTheme}
            title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            className={`theme-toggle p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 ${className}`}
        >
            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
    );
};

const VerticalNavbar = () => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const { user, logout, isAdmin, isXD, isLector, isMaster, isSST, isValidador } = useAuth();
    const { items: menuItems, loading: menuLoading } = useMenu();
    const location = useLocation();
    const [expanded, setExpanded] = useState(new Set());

    const handleLogout = () => {
        logout();
    };

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    const toggleMobile = () => {
        setIsMobileOpen(!isMobileOpen);
    };

    // Entre todos los ítems del menú cuyo path matchea la URL actual (exacto,
    // o como "carpeta" — ej. "/inspecciones" sigue activo en
    // "/inspecciones/historial"), gana el más específico (el path más largo).
    // Sin esto, "/rrhh" (Dashboard de RRHH) y "/rrhh/empleados" (Empleados)
    // quedaban los dos marcados como activos al estar en Empleados, porque
    // "/rrhh/empleados" también empieza con "/rrhh/" — son hermanos, no
    // padre-hijo, aunque compartan el mismo prefijo de URL.
    const activePath = React.useMemo(() => {
        const candidatos = menuItems
            .map((it) => it.path)
            .filter((p) => p && (location.pathname === p || location.pathname.startsWith(`${p}/`)));
        if (candidatos.length === 0) return null;
        return candidatos.reduce((mejor, p) => (p.length > mejor.length ? p : mejor));
    }, [menuItems, location.pathname]);

    const isActive = (path) => !!path && path === activePath;

    // Árbol de navegación a partir del catálogo plano de la BD (parent_key).
    const navTree = buildMenuTree(menuItems);

    // Auto-expande el grupo que contiene la ruta activa.
    React.useEffect(() => {
        const grupoActivo = (nodes, ancestros = []) => {
            for (const node of nodes) {
                if (isActive(node.path)) return ancestros;
                const encontrado = grupoActivo(node.children, [...ancestros, node.menu_key]);
                if (encontrado) return encontrado;
            }
            return null;
        };
        const claves = grupoActivo(navTree);
        if (claves && claves.length) {
            setExpanded((prev) => new Set([...prev, ...claves]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, menuItems.length]);

    const toggleGrupo = (menuKey) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(menuKey)) next.delete(menuKey);
            else next.add(menuKey);
            return next;
        });
    };

    return (
        <>
            {/* Mobile Header */}
            <div className="lg:hidden bg-white shadow-sm border-b px-4 py-3 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-lg">A</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">Argus</span>
                    {isAdmin() && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
                            <Shield className="h-3 w-3 inline mr-1" />
                            Admin
                        </span>
                    )}
                    {isXD() && (
                        <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full font-medium">
                            XD
                        </span>
                    )}
                    {isLector() && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                            Lector
                        </span>
                    )}
                    {isMaster() && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">
                            Master
                        </span>
                    )}
                    {isSST() && (
                        <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full font-medium">
                            <ShieldCheck className="h-3 w-3 inline mr-1" />
                            SST
                        </span>
                    )}
                    {isValidador() && (
                        <span className="bg-teal-100 text-teal-800 text-xs px-2 py-1 rounded-full font-medium">
                            Validador
                        </span>
                    )}
                </div>

                <div className="flex items-center space-x-1">
                    <ThemeToggleButton />
                    <NotificationBell />
                    <button
                        onClick={toggleMobile}
                        className="text-gray-600 hover:text-gray-900 p-2"
                    >
                        {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Sidebar Overlay */}
            {isMobileOpen && (
                <div className="lg:hidden fixed inset-0 z-50 flex">
                    <div className="fixed inset-0 bg-black opacity-50" onClick={toggleMobile}></div>
                    <div className="relative bg-white w-64 h-full shadow-xl">
                        <MobileSidebar
                            navTree={navTree}
                            isActive={isActive}
                            expanded={expanded}
                            onToggle={toggleGrupo}
                            user={user}
                            handleLogout={handleLogout}
                            toggleMobile={toggleMobile}
                            isAdmin={isAdmin()}
                            isXDRole={isXD()}
                        />
                    </div>
                </div>
            )}

            {/* Desktop Sidebar */}
            <div className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 transition-all duration-300 ${
                isCollapsed ? 'lg:w-16' : 'lg:w-64'
            } bg-white shadow-lg border-r border-gray-200 z-40`}>

                {/* Logo and Collapse Button */}
                <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
                    {!isCollapsed && (
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">A</span>
                            </div>
                            <span className="text-xl font-bold text-gray-900">Argus</span>
                            {isAdmin() && (
                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
                                    <Shield className="h-3 w-3 inline mr-1" />
                                    Admin
                                </span>
                            )}
                            {isXD() && (
                                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full font-medium">
                                    XD
                                </span>
                            )}
                            {isLector() && (
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                                    Lector
                                </span>
                            )}
                            {isMaster() && (
                                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">
                                    Master
                                </span>
                            )}
                        </div>
                    )}

                    <div className="flex items-center space-x-1">
                        <ThemeToggleButton />
                        {!isCollapsed && <NotificationBell />}
                        <button
                            onClick={toggleCollapse}
                            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                        >
                            {isCollapsed ? (
                                <ChevronRight className="h-5 w-5" />
                            ) : (
                                <ChevronLeft className="h-5 w-5" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Navigation Items */}
                <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                    <NavTree
                        nodes={navTree}
                        isActive={isActive}
                        isCollapsed={isCollapsed}
                        expanded={expanded}
                        onToggle={toggleGrupo}
                    />
                </nav>

                {/* User Section */}
                {user && (
                    <div className="border-t border-gray-200 p-3">
                        {!isCollapsed ? (
                            <div className="space-y-3">
                                <div className="flex items-center space-x-3 px-3 py-2">
                                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                                        <User className="h-4 w-4 text-gray-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {user.full_name}
                                        </p>
                                        <p className="text-xs text-gray-500 truncate">
                                            {user.email}
                                        </p>
                                        <p className="text-xs text-blue-600 font-medium">
                                            {user.role === 'admin' ? 'Administrador' :
                                             user.role === 'xd' ? 'XD' :
                                             user.role === 'master' ? 'Master' :
                                             user.role === 'lector' ? 'Lector' :
                                             user.role === 'sst' ? 'SST' :
                                             user.role === 'validador' ? 'Validador' : 'Usuario'}
                                        </p>
                                    </div>
                                </div>

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleLogout}
                                    className="w-full flex items-center justify-center space-x-2 text-red-600 border-red-300 hover:bg-red-50"
                                >
                                    <LogOut className="h-4 w-4" />
                                    <span>Cerrar Sesión</span>
                                </Button>
                            </div>
                        ) : (
                            <div className="flex flex-col space-y-2">
                                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mx-auto">
                                    <User className="h-4 w-4 text-gray-600" />
                                </div>
                                {isAdmin() && (
                                    <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center mx-auto">
                                        <Shield className="h-2 w-2 text-white" />
                                    </div>
                                )}
                                <button
                                    onClick={handleLogout}
                                    className="p-2 rounded-lg hover:bg-red-50 text-red-600 mx-auto"
                                    title="Cerrar Sesión"
                                >
                                    <LogOut className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

// Mobile Sidebar Component
const MobileSidebar = ({ navTree, isActive, expanded, onToggle, user, handleLogout, toggleMobile, isAdmin, isXDRole }) => (
    <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center space-x-2 px-4 py-4 border-b">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">F</span>
            </div>
            <span className="text-xl font-bold text-gray-900">Argus</span>
            {isAdmin && (
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
                    <Shield className="h-3 w-3 inline mr-1" />
                    Admin
                </span>
            )}
            {isXDRole && (
                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full font-medium">
                    XD
                </span>
            )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            <NavTree
                nodes={navTree}
                isActive={isActive}
                isCollapsed={false}
                expanded={expanded}
                onToggle={onToggle}
                onNavigate={toggleMobile}
            />
        </nav>

        {/* User Section */}
        {user && (
            <div className="border-t border-gray-200 p-3">
                <div className="flex items-center space-x-3 px-3 py-2 mb-3">
                    <User className="h-8 w-8 text-gray-400" />
                    <div>
                        <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                        <p className="text-xs text-blue-600 font-medium">
                            {user.role === 'admin' ? 'Administrador' :
                             user.role === 'xd' ? 'XD' :
                             user.role === 'master' ? 'Master' :
                             user.role === 'lector' ? 'Lector' :
                             user.role === 'sst' ? 'SST' :
                             user.role === 'validador' ? 'Validador' : 'Usuario'}
                        </p>
                    </div>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center space-x-2 text-red-600 border-red-300"
                >
                    <LogOut className="h-4 w-4" />
                    <span>Cerrar Sesión</span>
                </Button>
            </div>
        )}
    </div>
);

export default VerticalNavbar;
